import { useState } from "react";
import { Copy, ExternalLink, Link2, Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";

const clean = (v) => v.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-{2,}/g, "-").slice(0, 40);

const SlugEditor = ({ slug, onSaved, onCancel }) => {
  const [value, setValue] = useState(slug);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const s = value.replace(/^-+|-+$/g, "");
    if (s.length < 3) { toast.error("Use at least 3 letters or numbers"); return; }
    setSaving(true);
    try { const { data } = await api.put("/settings/slug", { slug: s }); onSaved(data); toast.success("Link updated. Your old link still works."); }
    catch (e) { toast.error(errorMessage(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-3" data-testid="slug-editor">
      <div className="flex h-12 items-center rounded-xl border border-white/20 bg-card/10 pl-3 font-mono text-[15px]">
        <span className="text-muted-foreground">/q/</span>
        <input value={value} onChange={(e) => setValue(clean(e.target.value))} autoFocus data-testid="slug-input"
          className="h-full min-w-0 flex-1 bg-transparent pr-3 text-white outline-none" />
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">Lowercase letters, numbers and dashes. Links you shared before keep working.</p>
      <div className="mt-3 flex gap-2">
        <button type="button" onClick={save} disabled={saving} data-testid="slug-save-button"
          className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-[color:var(--brand)] font-semibold disabled:opacity-50">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save link
        </button>
        <button type="button" onClick={onCancel} data-testid="slug-cancel-button" className="h-12 rounded-xl border border-white/20 px-4 font-semibold">Cancel</button>
      </div>
    </div>
  );
};

export const PublicLinkCard = ({ slug, onSlugSaved }) => {
  const [editing, setEditing] = useState(false);
  const link = `${window.location.origin}/q/${slug}`;
  const copy = () => navigator.clipboard.writeText(link).then(() => toast.success("Link copied"), () => toast.error("Couldn't copy, select the link manually"));
  return (
    <div className="rounded-2xl bg-slate-900 p-5 text-white" data-testid="public-link-card">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Link2 className="h-4 w-4" />Your quote link</p>
        {!editing && (
          <button type="button" onClick={() => setEditing(true)} data-testid="slug-edit-button"
            className="flex h-9 items-center gap-1.5 rounded-lg px-2 text-sm font-semibold text-slate-300"><Pencil className="h-4 w-4" />Edit</button>
        )}
      </div>
      {editing ? <SlugEditor slug={slug} onCancel={() => setEditing(false)} onSaved={(u) => { onSlugSaved(u); setEditing(false); }} /> : (
        <>
          <p className="mt-2 break-all font-mono text-[15px]" data-testid="public-link-url">{link}</p>
          <p className="mt-1 text-sm text-muted-foreground">Add it to your website, Google profile, texts and social posts.</p>
          <div className="mt-4 flex gap-2">
            <button type="button" onClick={copy} data-testid="copy-public-link-button"
              className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-[color:var(--brand)] font-semibold active:scale-[0.98] transition-transform">
              <Copy className="h-4 w-4" /> Copy link
            </button>
            <a href={link} target="_blank" rel="noreferrer" data-testid="open-public-link-button"
              className="flex h-12 items-center justify-center gap-2 rounded-xl border border-white/20 px-4 font-semibold">
              <ExternalLink className="h-4 w-4" /> Open
            </a>
          </div>
        </>
      )}
    </div>
  );
};
